<template>
  <v-card flat class="pa-4">
    <!-- Snackbar -->
    <v-snackbar v-model="mensaje.snackbar" :timeout="mensaje.timeout" :color="mensaje.color" text top right>
      {{ mensaje.text }}
      <template v-slot:action="{ attrs }">
        <v-btn :color="mensaje.colorBtn" text v-bind="attrs" @click="mensaje.snackbar = false">Cerrar</v-btn>
      </template>
    </v-snackbar>

    <!-- Botón nuevo post -->
    <v-row class="mb-3">
      <v-col>
        <v-btn color="teal" dark @click="openPostDialog(null)">
          <v-icon left>mdi-plus</v-icon> Nuevo Post
        </v-btn>
      </v-col>
    </v-row>

    <!-- Tabla de posts -->
    <v-simple-table fixed-header>
      <template v-slot:default>
        <thead>
          <tr>
            <th>Id</th>
            <th>Título</th>
            <th>Categoría</th>
            <th>Fijado</th>
            <th>Activo</th>
            <th>Fecha</th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="post in posts" :key="post.id">
            <td>{{ post.id }}</td>
            <td>{{ post.title }}</td>
            <td>{{ post.category ? post.category.name : '' }}</td>
            <td>
              <v-icon :color="post.pinned ? 'orange' : 'grey'">{{ post.pinned ? 'mdi-pin' : 'mdi-pin-off' }}</v-icon>
            </td>
            <td>
              <v-switch :input-value="post.active" color="teal" dense hide-details
                @change="toggleActive(post, $event)"></v-switch>
            </td>
            <td>{{ formatDate(post.created_at) }}</td>
            <td>
              <v-btn icon color="teal" @click="openImagesModal(post)" :disabled="!post.id">
                <v-icon>mdi-image-multiple</v-icon>
              </v-btn>
              <v-btn icon color="blue" @click="previewPost(post)">
                <v-icon>mdi-eye</v-icon>
              </v-btn>
              <v-btn icon color="teal darken-1" @click="notificarSuscriptores(post)" title="Notificar suscriptores">
                <v-icon>mdi-email-send</v-icon>
              </v-btn>
              <v-btn icon color="primary" @click="openPostDialog(post)">
                <v-icon>mdi-pencil</v-icon>
              </v-btn>
              <v-btn icon color="red" @click="eliminarPost(post)">
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </td>
          </tr>
        </tbody>
      </template>
    </v-simple-table>

    <!-- Dialog crear/editar post -->
    <v-dialog v-model="dialogPost" max-width="800" persistent scrollable>
      <v-card>
        <v-card-title>{{ editandoPost ? 'Editar' : 'Nuevo' }} Post</v-card-title>
        <v-card-text>
          <v-form ref="formPost" v-model="validPost">
            <v-select v-model="postForm.category_id" :items="categorias" item-text="name" item-value="id"
              label="Categoría" :rules="[v => !!v || 'Seleccione categoría']" outlined dense></v-select>

            <v-text-field v-model="postForm.title" label="Título" :rules="[v => !!v || 'Requerido']"
              counter="255" @blur="sugerirUrlPost"></v-text-field>

            <v-text-field v-model="postForm.url_post" label="URL del post"
              hint="Se usa para la URL pública: /blog/mi-url-personalizada" persistent-hint
              :rules="[v => !!v || 'Requerido', v => /^[a-z0-9\-]+$/.test(v) || 'Solo minúsculas, números y guiones']"
              counter="300">
              <template v-slot:append>
                <v-btn icon small @click="generarUrlPost" title="Generar desde título">
                  <v-icon small>mdi-refresh</v-icon>
                </v-btn>
              </template>
            </v-text-field>

            <!-- Editor rico (body) -->
            <div class="mb-3">
              <label class="caption grey--text">Contenido</label>
              <div class="editor-container">
                <div ref="editorToolbar">
                  <button class="ql-bold"></button>
                  <button class="ql-italic"></button>
                  <button class="ql-underline"></button>
                  <button class="ql-strike"></button>
                  <button class="ql-list" value="ordered"></button>
                  <button class="ql-list" value="bullet"></button>
                  <button class="ql-link"></button>
                  <button class="ql-clean"></button>
                </div>
                <div ref="editorBody" style="min-height: 150px;"></div>
              </div>
            </div>

            <v-divider class="my-3"></v-divider>
            <p class="subtitle-2 grey--text">Media (separada del texto)</p>

            <v-text-field v-model="postForm.video_url" label="URL del video (YouTube, Vimeo, etc.)"
              hint="Opcional" persistent-hint clearable></v-text-field>

            <v-text-field v-model="postForm.extra_url" label="URL extra (enlace externo)"
              hint="Opcional" persistent-hint clearable></v-text-field>

            <v-alert type="info" dense text class="mt-2">
              <v-icon left small>mdi-information</v-icon>
              Las imágenes se gestionan desde el botón
              <v-icon small>mdi-image-multiple</v-icon> en la tabla de posts (después de guardar).
            </v-alert>

            <!-- Imágenes existentes (en modo edición) -->
            <div v-if="editandoPost && postForm.images && postForm.images.length" class="mt-2">
              <p class="caption">Imágenes actuales: {{ postForm.images.length }}</p>
              <v-chip v-for="img in postForm.images" :key="img.id" small class="mr-1 mb-1">
                <v-icon left small>mdi-image</v-icon>
                {{ img.img_url }}
              </v-chip>
            </div>

            <v-divider class="my-3"></v-divider>

            <!-- Layout drag 2x2 -->
            <p class="subtitle-2 grey--text mb-2">Orden de visualización (arrastrar para reordenar)</p>

            <!-- Color global -->
            <v-row align="center" class="mb-2" no-gutters>
              <v-col cols="auto" class="mr-2">
                <v-menu :close-on-content-click="false" offset-y>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn small outlined v-bind="attrs" v-on="on">
                      <v-icon left small>mdi-palette</v-icon>
                      Color global
                      <v-avatar size="18" class="ml-2" :color="globalColor"></v-avatar>
                    </v-btn>
                  </template>
                  <v-color-picker v-model="globalColor" hide-inputs
                    :swatches="colorSwatches" show-swatches mode="hexa"></v-color-picker>
                </v-menu>
              </v-col>
              <v-col cols="auto">
                <v-btn small color="teal" dark @click="applyGlobalColor">
                  <v-icon left small>mdi-format-paint</v-icon> Aplicar a todos
                </v-btn>
              </v-col>
            </v-row>

            <div id="forumLayoutGrid" class="layout-grid mb-3">
              <div v-for="(item, idx) in postForm.layout_order" :key="item.section"
                class="layout-cell" :style="{ backgroundColor: item.color }">
                <v-icon small class="drag-handle mr-1">mdi-drag</v-icon>
                <v-icon small class="mr-1">{{ sectionIcon(item.section) }}</v-icon>
                <span class="caption font-weight-medium">{{ sectionLabel(item.section) }}</span>
                <v-spacer></v-spacer>
                <v-menu :close-on-content-click="false" offset-y>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon x-small v-bind="attrs" v-on="on">
                      <v-icon small color="grey darken-2">mdi-palette</v-icon>
                    </v-btn>
                  </template>
                  <v-color-picker v-model="postForm.layout_order[idx].color" hide-inputs
                    :swatches="colorSwatches" show-swatches mode="hexa"></v-color-picker>
                </v-menu>
              </div>
            </div>

            <v-divider class="my-3"></v-divider>

            <v-row>
              <v-col cols="6">
                <v-switch v-model="postForm.pinned" label="Fijar post" color="orange"></v-switch>
              </v-col>
              <v-col cols="6">
                <v-switch v-model="postForm.active" label="Publicar (activo)" color="teal"></v-switch>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-btn text color="blue" @click="previewLocal">
            <v-icon left>mdi-eye</v-icon> Preview
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn text @click="closePostDialog">Cancelar</v-btn>
          <v-btn color="teal" dark @click="guardarPost">{{ editandoPost ? 'Actualizar' : 'Guardar' }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog preview -->
    <forum-post-preview v-model="dialogPreview" :post="previewData" :preview-mode="previewMode"></forum-post-preview>

    <!-- Modal gestión de imágenes -->
    <forum-post-images-modal v-model="dialogImages" :post-id="imagesPostId" :post-title="imagesPostTitle"
      @updated="listarPosts"></forum-post-images-modal>

    <!-- Dialog notificar suscriptores -->
    <forum-notify-dialog ref="notifyDialog"
      @notificado="onNotificado"
      @error="onNotifyError">
    </forum-notify-dialog>
  </v-card>
</template>

<script>
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import Sortable from 'sortablejs';
import forumService from '@/services/forumService.js';
import ForumPostPreview from '@/components/forum/ForumPostPreview.vue';
import ForumPostImagesModal from '@/components/forum/ForumPostImagesModal.vue';
import ForumNotifyDialog from '@/components/forum/ForumNotifyDialog.vue';

export default {
  name: 'ForumPostAdmin',
  components: { ForumPostPreview, ForumPostImagesModal, ForumNotifyDialog },
  data() {
    return {
      posts: [],
      categorias: [],
      dialogPost: false,
      editandoPost: false,
      validPost: true,
      quill: null,
      postForm: {
        id: null,
        category_id: null,
        title: '',
        url_post: '',
        body: '',
        video_url: '',
        extra_url: '',
        pinned: false,
        active: false,
        layout_order: [
          { section: 'text', color: '#E3F2FD' },
          { section: 'images', color: '#E8F5E9' },
          { section: 'video', color: '#FFF3E0' },
          { section: 'comments', color: '#F3E5F5' }
        ],
        images: []
      },
      sortableInstance: null,
      dialogPreview: false,
      previewData: {},
      previewMode: 'local',
      dialogImages: false,
      imagesPostId: null,
      imagesPostTitle: '',
      colorSwatches: [
        ['#E3F2FD', '#BBDEFB', '#90CAF9'],
        ['#E8F5E9', '#C8E6C9', '#A5D6A7'],
        ['#FFF3E0', '#FFE0B2', '#FFCC80'],
        ['#F3E5F5', '#E1BEE7', '#CE93D8'],
        ['#ECEFF1', '#CFD8DC', '#B0BEC5'],
        ['#FFF9C4', '#FFF176', '#FFEE58'],
        ['#FFEBEE', '#FFCDD2', '#EF9A9A'],
        ['#E0F7FA', '#B2EBF2', '#80DEEA']
      ],
      globalColor: '#E3F2FD',
      mensaje: {
        snackbar: false,
        text: '',
        color: '',
        colorBtn: '',
        timeout: 4000
      }
    };
  },
  mounted() {
    this.listarPosts();
    this.listarCategorias();
  },
  methods: {
    listarPosts() {
      forumService.listarPostsAdmin().then(res => {
        this.posts = res.data || [];
      });
    },
    listarCategorias() {
      forumService.listarCategorias().then(res => {
        this.categorias = res.data;
      });
    },
    openPostDialog(post) {
      const defaultLayout = [
        { section: 'text', color: '#E3F2FD' },
        { section: 'images', color: '#E8F5E9' },
        { section: 'video', color: '#FFF3E0' },
        { section: 'comments', color: '#F3E5F5' }
      ];
      if (post) {
        this.editandoPost = true;
        this.postForm = {
          id: post.id,
          category_id: post.category_id || (post.category ? post.category.id : null),
          title: post.title,
          url_post: post.url_post || '',
          body: post.body || '',
          video_url: post.video_url || '',
          extra_url: post.extra_url || '',
          pinned: post.pinned,
          active: post.active,
          layout_order: post.layout_order || defaultLayout,
          images: post.images || []
        };
      } else {
        this.editandoPost = false;
        this.postForm = {
          id: null,
          category_id: null,
          title: '',
          url_post: '',
          body: '',
          video_url: '',
          extra_url: '',
          pinned: false,
          active: false,
          layout_order: JSON.parse(JSON.stringify(defaultLayout)),
          images: []
        };
      }
      this.dialogPost = true;
      this.$nextTick(() => {
        this.initQuill();
        this.initSortable();
      });
    },
    closePostDialog() {
      this.dialogPost = false;
      if (this.quill) {
        this.quill.root.innerHTML = '';
        this.quill = null;
      }
      if (this.sortableInstance) {
        this.sortableInstance.destroy();
        this.sortableInstance = null;
      }
    },
    initQuill() {
      // Destruir instancia previa si existe
      if (this.quill) {
        this.quill.root.innerHTML = '';
        this.quill = null;
      }
      if (!this.$refs.editorBody) return;
      // Limpiar el contenido del DOM antes de crear nueva instancia
      this.$refs.editorBody.innerHTML = '';
      this.quill = new Quill(this.$refs.editorBody, {
        theme: 'snow',
        modules: {
          toolbar: this.$refs.editorToolbar
        }
      });
      // Setear contenido existente (edición) o vacío (nuevo)
      if (this.postForm.body) {
        this.quill.root.innerHTML = this.postForm.body;
      }
    },
    guardarPost() {
      if (!this.$refs.formPost.validate()) return;

      // Obtener contenido del editor
      if (this.quill) {
        this.postForm.body = this.quill.root.innerHTML;
      }

      if (!this.postForm.body || this.postForm.body === '<p><br></p>') {
        this.showMsg('El contenido es requerido', 'error');
        return;
      }

      if (this.editandoPost) {
        // Update
        const data = {
          category_id: this.postForm.category_id,
          title: this.postForm.title,
          url_post: this.postForm.url_post,
          body: this.postForm.body,
          video_url: this.postForm.video_url || null,
          extra_url: this.postForm.extra_url || null,
          pinned: this.postForm.pinned,
          active: this.postForm.active,
          layout_order: this.postForm.layout_order
        };
        forumService.updatePost(this.postForm.id, data).then(() => {
          this.showMsg('Post actualizado', 'success');
          this.closePostDialog();
          this.listarPosts();
        }).catch(err => {
          this.handleError(err);
        });
      } else {
        // Crear (FormData no soporta arrays nativamente, enviar como JSON)
        const data = {
          category_id: this.postForm.category_id,
          title: this.postForm.title,
          url_post: this.postForm.url_post,
          body: this.postForm.body,
          video_url: this.postForm.video_url || null,
          extra_url: this.postForm.extra_url || null,
          pinned: this.postForm.pinned,
          active: this.postForm.active,
          layout_order: this.postForm.layout_order
        };
        forumService.savePost(data).then(() => {
          this.showMsg('Post creado. Use el botón de imágenes para agregar fotos.', 'success');
          this.closePostDialog();
          this.listarPosts();
        }).catch(err => {
          this.handleError(err);
        });
      }
    },
    openImagesModal(post) {
      this.imagesPostId = post.id;
      this.imagesPostTitle = post.title;
      this.dialogImages = true;
    },
    toggleActive(post, value) {
      forumService.updatePost(post.id, { ...post, active: value }).then(() => {
        post.active = value;
        this.showMsg(value ? 'Post publicado' : 'Post ocultado', 'success');
      }).catch(() => {
        this.showMsg('Error al cambiar estado', 'error');
      });
    },
    eliminarPost(post) {
      if (!confirm('¿Eliminar el post "' + post.title + '"?')) return;
      forumService.deletePost(post.id).then(() => {
        this.showMsg('Post eliminado', 'success');
        this.listarPosts();
      }).catch(() => {
        this.showMsg('Error al eliminar', 'error');
      });
    },
    previewPost(post) {
      const defaultLayout = [
        { section: 'text', color: '#E3F2FD' },
        { section: 'images', color: '#E8F5E9' },
        { section: 'video', color: '#FFF3E0' },
        { section: 'comments', color: '#F3E5F5' }
      ];
      this.previewData = {
        ...post,
        categoryName: post.category ? post.category.name : '',
        categoryIcon: post.category ? post.category.icon : '',
        imageUrls: (post.images || []).map(img => forumService.getImageUrl(post.id, img.img_url)),
        layout_order: post.layout_order || defaultLayout
      };
      this.previewMode = 'saved';
      this.dialogPreview = true;
    },
    previewLocal() {
      if (this.quill) {
        this.postForm.body = this.quill.root.innerHTML;
      }
      const cat = this.categorias.find(c => c.id === this.postForm.category_id);
      let imageUrls = [];
      if (this.editandoPost && this.postForm.images && this.postForm.images.length) {
        imageUrls = this.postForm.images.map(img => forumService.getImageUrl(this.postForm.id, img.img_url));
      }
      this.previewData = {
        title: this.postForm.title || '(Sin título)',
        body: this.postForm.body || '',
        video_url: this.postForm.video_url,
        categoryName: cat ? cat.name : '',
        categoryIcon: cat ? cat.icon : '',
        imageUrls: imageUrls,
        layout_order: this.postForm.layout_order
      };
      this.previewMode = 'local';
      this.dialogPreview = true;
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      return d.toLocaleDateString('es', { day: '2-digit', month: '2-digit', year: 'numeric' });
    },
    handleError(err) {
      const errors = err.response && err.response.data && err.response.data.errors;
      this.showMsg(errors ? errors.join(', ') : 'Error al guardar', 'error');
    },
    showMsg(text, type) {
      this.mensaje.text = text;
      this.mensaje.color = type === 'success' ? 'success' : 'error';
      this.mensaje.colorBtn = type === 'success' ? 'green darken-1' : 'red darken-1';
      this.mensaje.snackbar = true;
    },
    // ─── Layout helpers ─────────────────────────────────────────
    initSortable() {
      this.$nextTick(() => {
        const el = document.getElementById('forumLayoutGrid');
        if (!el) return;
        if (this.sortableInstance) {
          this.sortableInstance.destroy();
        }
        this.sortableInstance = new Sortable(el, {
          animation: 150,
          handle: '.drag-handle',
          onEnd: this.onLayoutReorder
        });
      });
    },
    onLayoutReorder({ oldIndex, newIndex }) {
      const moved = this.postForm.layout_order.splice(oldIndex, 1)[0];
      this.postForm.layout_order.splice(newIndex, 0, moved);
    },
    applyGlobalColor() {
      this.postForm.layout_order.forEach(item => {
        item.color = this.globalColor;
      });
      this.$forceUpdate();
    },
    generarUrlPost() {
      if (!this.postForm.title) return;
      this.postForm.url_post = this.postForm.title
        .toLowerCase()
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    },
    sugerirUrlPost() {
      // Solo sugerir si el campo está vacío (no sobreescribir si ya lo editó manualmente)
      if (!this.postForm.url_post && this.postForm.title) {
        this.generarUrlPost();
      }
    },
    sectionIcon(section) {
      const icons = {
        text: 'mdi-text-box',
        images: 'mdi-image-multiple',
        video: 'mdi-video',
        comments: 'mdi-comment-multiple'
      };
      return icons[section] || 'mdi-help';
    },
    sectionLabel(section) {
      const labels = {
        text: 'Texto + Me Gusta',
        images: 'Imágenes',
        video: 'Video',
        comments: 'Comentarios'
      };
      return labels[section] || section;
    },
    notificarSuscriptores(post) {
      this.$refs.notifyDialog.open(post);
    },
    onNotificado(data) {
      this.showMsg(data.message || 'Notificación enviada', 'success');
    },
    onNotifyError(msg) {
      this.showMsg(msg || 'Error al enviar notificaciones', 'error');
    }
  }
};
</script>

<style scoped>
.editor-container {
  border: 1px solid #ccc;
  border-radius: 4px;
}
.layout-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: auto auto;
  gap: 8px;
}
.layout-cell {
  padding: 12px 16px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  min-height: 50px;
  border: 1px solid rgba(0,0,0,0.1);
}
.drag-handle {
  cursor: grab;
}
</style>
